//Third party libraries
import * as Yup from 'yup';

//Custom components
import Button from '../../UI/Button';

//Assets andd Miscellaneous
import { HookFormProvider } from '../../UI/HookForm/Provider';
import { HookForm } from '../../UI/HookForm/Form';
import { HookInput } from '../../UI/HookForm/Form/Input';
import { emailRegEx } from '../../../utils/RegularExpressions';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { notifyUser } from '../../../store/Toast';
import {
  IPostSherpaVendorsPayload,
  postSherpaVendors,
} from '../../../api/SVTN/Vendor';
import { PostVendorInvitePayload } from '../../../api/SVTN/Vendor/types';
import { ReactQueryKey } from '../../../constants/ReactQueryKeys';
import { sanitizeResponseError } from '../../../utils';

const validationSchema = Yup.object({
  name: Yup.string().required('Organization Name is required'),
  contactName: Yup.string().required('Contact Name is required'),
  contactEmail: Yup.string()
    .required('Required.')
    .matches(emailRegEx, 'Please enter valid e-mail address'),
});

interface NewVendorFormProps {
  handleModal: () => void;
}

//Component Begin
export default function NewVendorForm({ handleModal }: NewVendorFormProps) {
  const dispatch = useDispatch();
  const query = useQueryClient();

  const postSendInvitation = useMutation(
    (data: IPostSherpaVendorsPayload) => postSherpaVendors({ data }),
    {
      onSuccess: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Vendor invited successfully.`,
            toastType: 'success',
          })
        );
        handleModal();
        query.invalidateQueries([ReactQueryKey.GET_VENDORS]);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  async function sendInvitation(formData: PostVendorInvitePayload) {
    const data = {
      organizationName: formData?.name,
      email: formData?.contactEmail,
    };
    postSendInvitation.mutate(data);
  }

  return (
    <HookFormProvider mode='all' resolver={yupResolver(validationSchema)}>
      <HookForm
        className='space-y-5'
        noDefaultClass={true}
        onSubmit={sendInvitation}
      >
        <h3 className='mt-0 text-primaryColor-normal'>New Vendor</h3>
        <p className='text-tertiaryColor-normal'>
          {postSendInvitation?.error?.response?.data?.message}
        </p>
        <section className='grid grid-cols-6 gap-5'>
          <HookInput
            customClassName='col-span-6'
            type='text'
            id='OrganizationName'
            name='name'
            label='Organization Name'
          />
          <HookInput
            customClassName='col-span-6'
            type='text'
            id='contactName'
            name='contactName'
            label='Contact Name'
          />
          <HookInput
            customClassName='col-span-6'
            id='email'
            type='text'
            name='contactEmail'
            label='E-mail'
          />
        </section>
        <div className='flex justify-end gap-4'>
          <Button
            label='Cancel'
            btnStyle='btn-neutral'
            type='button'
            disabled={postSendInvitation.isLoading}
            onClick={handleModal}
          />
          <Button
            label={`${
              postSendInvitation.isLoading ? 'Sending invite...' : 'Send Invite'
            }`}
            btnStyle='btn-primary'
            type='submit'
            disabled={postSendInvitation.isLoading}
          />
        </div>
      </HookForm>
    </HookFormProvider>
  );
}
